/**
 * AI 도구(신) — 콘솔 목록 공통 규격 채택본 (2026-09-09 사용자 지시).
 *
 * 이전 화면 `AiToolsSection`(메뉴 「AI 도구」)은 **그대로 둔다** — 이것은 **새 메뉴**다.
 * 둘을 나란히 두고 눈으로 견준 뒤 옛 것을 내리는 순서다.
 *
 * 무엇이 달라졌나 — 세로로 쌓여 있던 네 절을 한 화면으로 접었다:
 *   ① 「연결된 서버」 카드 그리드  → **좌 분류 240**(전체 · 연결 서버 · 내장 서버 · 사용자 정의)
 *   ② 「연결된 도구」 도메인 패널  → **표 한 벌** + 도메인은 툴바 칩으로 좁힌다
 *   ③ 「도구 생성기」 위저드 상주  → **툴바의 「도구 등록」 단추 → 팝업**
 *      (정본 P3-1: 가끔 하는 일에 본문 자리를 상시로 내주지 않는다)
 *   ④ 「사용자 정의 도구」 절       → **좌 분류의 한 갈래**로 흡수. 같은 도구를 두 번 세지 않는다.
 *
 * ★ 기능은 기존 화면에 맞춘다(사용자 지시) — **삭제는 사용자 정의 도구에만** 있고,
 *   기본 제공 도구는 단추를 **잠근다**(감추지 않는다. 왜 못 지우는지는 title 로).
 * ★★ **답변 톤·포맷은 이 화면에서 다루지 않는다**(2026-09-09 사용자 지시).
 *   옛 화면은 「말투는 답변 톤, 표시 형식은 답변 포맷 메뉴에서 설정합니다」라고 적어 놓고도
 *   그 저장바를 갖고 있었다 — 같은 값을 두 자리에서 고치면 어느 쪽이 최신인지 모른다.
 *   ⚠ **저장바만 뺄 수는 없었다.** 톤·포맷 편집은 `useStyleConfig` 의 로컬 상태에 쌓였다가
 *     `saveAll()`(저장바) 로만 서버에 간다 — 저장바만 없애면 «고쳤는데 저장이 안 되는» 화면이
 *     된다. 그래서 도메인 기본 줄·도구별 포맷 고르기까지 함께 걷어냈고, 어디서 하는지는
 *     화면 설명에 적었다.
 * ⚠ 서버 변경 0 · 마이그레이션 0 — 부르는 라우트가 이전과 똑같다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import './AiToolsCatalog.css';
import './AiToolsGenerator.css';
import { Info, Trash2, RefreshCw, Sparkles, Globe, HardDrive, Cpu, X } from '../../../icons';
import {
  listAiTools, listAiToolConnections, deleteCustomAiTool,
  type AiToolsListResult, type AiToolConnection, type AiToolMeta,
} from '../../../api/aiTools';
import {
  CUSTOM_KINDS, GEN_MODES, NATIVE_SERVERS,
  accentForDomain, fmtAge, toolConnId, isNativeTool, groupByDomain,
  ToolDetailModal, DbGenerator, SwaggerImporter, BriefGenerator,
} from './AiToolsSection';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';

const DOMAIN_LABEL: Record<string, string> = {
  chat: '채팅', hr: '인사', calendar: '캘린더', wiki: '위키', static: '기본',
};
function domainOf(t: AiToolMeta): string {
  return t.domain || (t.name.includes('.') ? t.name.split('.')[0]! : 'static');
}
function isCustom(t: AiToolMeta): boolean {
  const meta = (t._meta && typeof t._meta === 'object' ? t._meta : {}) as Record<string, unknown>;
  return CUSTOM_KINDS.has(String(meta.tool_kind ?? t.tool_kind ?? ''));
}
function errMsg(e: unknown): string {
  const d = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  return d?.message ?? d?.error ?? (e as Error)?.message ?? '처리하지 못했습니다.';
}

/**
 * 좌 분류의 키는 **접두사로 축을 나눈다** — `all` · `custom` · `conn:<id>` · `native:<domain>`.
 * 이 화면만 축이 둘이라(서버 · 종류) 한 목록에 섞여 서는데, 접두사가 그 둘을 가른다.
 */

export function AiToolsListSection() {
  const [data, setData] = useState<AiToolsListResult | null>(null);
  const [connections, setConnections] = useState<AiToolConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pick, setPick] = useState<string>('all');
  const [domain, setDomain] = useState<string>('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<AiToolMeta | null>(null);
  const [genOpen, setGenOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [tools, conns] = await Promise.all([listAiTools(), listAiToolConnections()]);
      setData(tools);
      setConnections(conns.connections ?? []);
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const tools = data?.tools ?? [];

  /** 내장 서버 — connections 에 없고 도구 카탈로그에만 있다. 도메인이 곧 서버다. */
  const natives = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of tools) if (isNativeTool(t)) m[domainOf(t)] = (m[domainOf(t)] ?? 0) + 1;
    return Object.keys(NATIVE_SERVERS).filter((d) => m[d]).map((d) => ({ domain: d, n: m[d]! }));
  }, [tools]);

  /** 고른 갈래로 좁힌 도구 — 도메인 칩은 여기 위에 한 겹 더 얹는다. */
  const inPick = useMemo(() => {
    if (pick === 'all') return tools;
    if (pick === 'custom') return tools.filter(isCustom);
    if (pick.startsWith('conn:')) { const id = pick.slice(5); return tools.filter((t) => toolConnId(t) === id); }
    if (pick.startsWith('native:')) { const d = pick.slice(7); return tools.filter((t) => isNativeTool(t) && domainOf(t) === d); }
    return tools;
  }, [tools, pick]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return inPick.filter((t) => {
      if (domain !== 'all' && domainOf(t) !== domain) return false;
      if (!needle) return true;
      return `${t.name} ${t.description ?? ''}`.toLowerCase().includes(needle);
    });
  }, [inPick, domain, q]);

  /** 도메인 칩 — 「누르면 몇 개 보이나」는 **고른 갈래 기준**이다(정본 P3). */
  const domainChips = useMemo(() => {
    const by = groupByDomain(inPick);
    const keys = Object.keys(by).sort();
    return [
      { key: 'all', label: '전체', count: inPick.length },
      ...keys.map((d) => ({ key: d, label: DOMAIN_LABEL[d] ?? d, count: by[d]!.length })),
    ];
  }, [inPick]);

  const cacheByDomain = useMemo(() => {
    const m: Record<string, number | null | undefined> = {};
    for (const c of data?.cache_stats ?? []) m[c.domain] = c.age_ms;
    return m;
  }, [data]);

  async function removeTool(t: AiToolMeta) {
    if (!window.confirm(`도구 "${t.name}" 을(를) 삭제할까요?`)) return;
    setBusy(t.name);
    try { await deleteCustomAiTool(t.name); await load(); }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  }

  const sideItems = [
    { key: 'all', label: '전체', count: tools.length, icon: <Cpu size={16} /> },
    ...connections.map((c) => ({
      key: `conn:${c.id}`,
      label: c.label || c.id,
      count: tools.filter((t) => toolConnId(t) === c.id).length,
      icon: c.kind === 'http' ? <Globe size={16} /> : <HardDrive size={16} />,
    })),
    ...natives.map((n) => ({
      key: `native:${n.domain}`,
      label: NATIVE_SERVERS[n.domain]?.label ?? n.domain,
      count: n.n,
      icon: <HardDrive size={16} />,
    })),
    { key: 'custom', label: '사용자 정의', count: tools.filter(isCustom).length, icon: <Sparkles size={16} /> },
  ];

  return (
    <>
      <ConsoleListScreen
        title="AI 도구"
        kpi={`도구 ${tools.length} · 서버 ${connections.length + natives.length}`}
        desc="오케스트레이터가 부를 수 있는 도구와 연결된 서버를 관리합니다 — 서버 전역 설정입니다. 답변의 말투는 「답변 톤」, 표시 형식은 「답변 포맷」 메뉴에서 정합니다."
        search={{ value: q, onChange: setQ, placeholder: '도구 이름 · 설명으로 찾기' }}
        filters={{ active: domain, onChange: setDomain, items: domainChips }}
        toolsExtra={(
          <button type="button" className="cl-chip" disabled={loading} onClick={() => void load()}>
            <RefreshCw size={14} /> 새로고침
          </button>
        )}
        add={{ label: '도구 등록', onClick: () => setGenOpen(true) }}
        side={{
          head: '서버',
          active: pick,
          onChange: (k) => { setPick(k); setDomain('all'); },
          items: sideItems,
        }}
        notice={error ? <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div> : null}
      >
        {loading ? (
          <div className="cl-empty">불러오는 중…</div>
        ) : (
          <ConsoleListTable
            rows={shown}
            rowKey={(t) => t.name}
            onRowClick={(t) => setDetail(t)}
            empty={tools.length === 0
              ? <>등록된 도구가 없습니다.<span className="cl-empty__sub">위의 「도구 등록」으로 만듭니다.</span></>
              : <>찾는 도구가 없습니다.<span className="cl-empty__sub">검색어나 갈래를 지워 보세요.</span></>}
            columns={[
              {
                key: 'name', header: '도구',
                render: (t) => (
                  <span className="cl-name">
                    <span className="cl-name__t" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.82rem' }}>{t.name}</span>
                    <span className="cl-name__s">{t.description}</span>
                  </span>
                ),
              },
              {
                key: 'domain', header: '도메인',
                render: (t) => {
                  const d = domainOf(t);
                  const a = accentForDomain(d);
                  return (
                    <span className="cl-badge" style={{ background: a.t, color: a.c }}>
                      {DOMAIN_LABEL[d] ?? d}
                    </span>
                  );
                },
              },
              {
                key: 'kind', header: '종류',
                render: (t) => (isCustom(t)
                  ? <span className="cl-badge is-on">사용자 정의</span>
                  : <span className="cl-badge">기본 제공</span>),
              },
              {
                key: 'cache', header: '캐시',
                render: (t) => <span className="cl-name__s">{fmtAge(cacheByDomain[domainOf(t)])}</span>,
              },
            ]}
            rowActions={(t) => (
              <>
                <button
                  type="button" className="cl-rowbtn"
                  aria-label={`${t.name} 상세`} title="상세"
                  onClick={() => setDetail(t)}
                >
                  <Info size={13} />
                </button>
                {/* ★ 기존 화면과 같다 — 삭제는 «도구 생성기로 만든» 사용자 정의 도구에만.
                    기본 제공 도구는 잠그고 왜 못 지우는지를 적는다(정본 P3: 감추지 않는다). */}
                <button
                  type="button" className="cl-rowbtn cl-rowbtn--danger"
                  disabled={!isCustom(t) || busy === t.name}
                  aria-label={`${t.name} 삭제`}
                  title={isCustom(t) ? '삭제' : '기본 제공 도구는 지울 수 없습니다 — 도구 등록으로 만든 것만 지웁니다'}
                  onClick={() => void removeTool(t)}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        )}
      </ConsoleListScreen>

      {detail && (
        <ToolDetailModal
          tool={detail}
          accent={accentForDomain(domainOf(detail))}
          domainLabel={DOMAIN_LABEL[domainOf(detail)] ?? domainOf(detail)}
          domain={domainOf(detail)}
          onClose={() => setDetail(null)}
        />
      )}

      {genOpen && (
        <ToolGeneratorModal
          connections={connections}
          reload={load}
          onClose={() => setGenOpen(false)}
        />
      )}
    </>
  );
}

/**
 * 도구 등록 — 본문에 상주하던 위저드를 팝업으로 옮긴 것. 3모드·단계 안내·패널은 그대로다.
 * ⚠ 저장하면 목록을 되읽지만 팝업은 닫지 않는다 — 위저드가 「후보 여러 개를 골라 저장」이라
 *   한 번 저장하고 이어서 더 담는 흐름이 있다(옛 화면도 그랬다).
 */
function ToolGeneratorModal({ connections, reload, onClose }: {
  connections: AiToolConnection[];
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState(GEN_MODES[0]!.key);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const cur = GEN_MODES.find((m) => m.key === mode)!;

  return (
    <div className="cl-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="cl-modal cl-modal--wide" role="dialog" aria-modal="true" aria-label="도구 등록"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cl-modal__head">
          <h3>도구 등록</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="cl-modal__body">
          <div className="ait-gen-modes" role="tablist" aria-label="도구 등록 방식">
            {GEN_MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              return (
                <button
                  key={m.key} type="button" role="tab" aria-selected={active}
                  className={`ait-gen-mode${active ? ' is-active' : ''}`}
                  style={{ ['--cat' as string]: m.accent.c, ['--cat-tint' as string]: m.accent.t }}
                  onClick={() => setMode(m.key)}
                >
                  <span className="ait-gen-mode__top">
                    <span className="ait-gen-mode__icon"><Icon /></span>
                    <span className="ait-gen-mode__title">{m.title}</span>
                  </span>
                  <span className="ait-gen-mode__desc">{m.desc}</span>
                </button>
              );
            })}
          </div>

          <div
            className="ait-gen-guide"
            style={{ ['--cat' as string]: cur.accent.c, ['--cat-tint' as string]: cur.accent.t }}
          >
            <span className="ait-gen-guide__lead">등록 단계</span>
            {cur.steps.map((s, i) => (
              <span key={s} className="ait-gen-step">
                {i > 0 && <span className="ait-gen-step__arrow" aria-hidden>→</span>}
                <span className="ait-gen-step__num">{i + 1}</span>
                <span className="ait-gen-step__label">{s}</span>
              </span>
            ))}
          </div>

          <div className="ait-gen-panel" key={mode}>
            {mode === 'db' && <DbGenerator connections={connections} reload={reload} />}
            {mode === 'swagger' && <SwaggerImporter onSaved={reload} />}
            {mode === 'brief' && <BriefGenerator connections={connections} reload={reload} />}
          </div>
        </div>
        <div className="cl-modal__foot">
          <button type="button" className="cl-modal__btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
